import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Users, Upload, FileText, X } from 'lucide-react';
import {  TemplateComponent } from '@/client/api/whatsapp/create-template.mutation';
import { toast } from 'sonner';
import { CreateContactPayload, useCreateContactMutation } from '@/client/api/whatsapp/create-contact.mutation';
import { useBulkContactUploadMutation } from '@/client/api/whatsapp/upload-contact.mutation';

interface CreateContactModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

const CreateContactModal: React.FC<CreateContactModalProps> = ({
  open,
  onOpenChange,
  onSuccess
}) => {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [tags, setTags] = useState<string[]>([]);

  const [tagInput, setTagInput] = useState('');
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [method, setMethod] = useState<'manual' | 'upload'>('manual');


  const [components, setComponents] = useState<(TemplateComponent & { variables?: { param_name: string; example: string }[] })[]>([
    { type: 'body', text: '', variables: [] }
  ]);

  const { mutate: createContact, isPending: createContactIsPending } = useCreateContactMutation();
  const { mutate: uploadContacts, isPending: uploadContactsIsPending } = useBulkContactUploadMutation();

  const addTag = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed || tags.includes(trimmed)) return;

    setTags(prev => [...prev, trimmed]);
  };

  const removeTag = (tagToRemove: string) => {
    setTags(prev => prev.filter(tag => tag !== tagToRemove));
  };

  const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(tagInput);
      setTagInput('');
    }
  };

  const handleAudienceTypeChange = (type: 'manual' | 'upload') => {
    setMethod(type);
    if (type === 'manual') {
      setCsvFile(null);
    }
  };

  const handleCsvUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setCsvFile(e.target.files[0]);
    }
  };

  const handleRemoveCsv = () => {
    setCsvFile(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if(method === 'manual') {
      if (!name) {
        toast.error('Template name is required');
        return;
      }

      const payload: CreateContactPayload = {
        contacts: [{
          name,
          phone,
          tags: tags,
          notes,
          metadata: {}
        }]
      };
  
      createContact(payload, {
        onSuccess: () => {
          toast.success('Contact created successfully and sent for review');
          onSuccess();
          onOpenChange(false);
          // Reset form
          setName('');
          setComponents([{ type: 'body', text: '', variables: [] }]);
        },
        onError: (err: any) => {
          toast.error(err.message || 'Failed to create template');
        }
      });
    }else {
      if (!csvFile) {
        toast.error('CSV file is required');
        return;
      }

      const formData = new FormData();
      formData.append('file', csvFile);
      
      uploadContacts(formData, {
        onSuccess: () => {
          toast.success('Contacts uploaded successfully and sent for review');
          onSuccess();
          onOpenChange(false);
          // Reset form
          setName('');
          setComponents([{ type: 'body', text: '', variables: [] }]);
        },
        onError: (err: any) => {
          toast.error(err.message || 'Failed to upload contacts');
        }
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create WhatsApp Contact</DialogTitle>
          <DialogDescription>
            Contacts must be approved by Meta before they can be used.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 py-4">
          <div className="flex gap-4 mb-6">
            <button
              type='button'
              onClick={() => handleAudienceTypeChange('manual')}
              className={`flex-1 p-4 border-2 rounded-lg transition-all text-center ${method === 'manual'
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
                }`}
            >
              <Users className={`w-6 h-6 mx-auto mb-2 ${method === 'manual' ? 'text-blue-500' : 'text-gray-400'
                }`} />
              <p className={`text-sm font-medium ${method === 'manual' ? 'text-blue-700' : 'text-gray-700'
                }`}>Add Manually</p>
            </button>
            <button
              type='button'
              onClick={() => handleAudienceTypeChange('upload')}
              className={`flex-1 p-4 border-2 rounded-lg transition-all text-center ${method === 'upload'
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
                }`}
            >
              <Upload className={`w-6 h-6 mx-auto mb-2 ${method === 'upload' ? 'text-blue-500' : 'text-gray-400'
                }`} />
              <p className={`text-sm font-medium ${method === 'upload' ? 'text-blue-700' : 'text-gray-700'
                }`}>Upload CSV List</p>
            </button>
          </div>


          {method === 'manual' ? (
            <div className="space-y-2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Contact Name *</Label>
                  <Input
                    id="name"
                    placeholder="e.g. Joseph Nartey"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone *</Label>
                  <Input
                    id="phone"
                    placeholder="e.g. +1234567890"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className='space-y-1'>
                <Label className="">Tags *</Label>
                {/* Tag list */}
                <div className="flex flex-wrap gap-2">
                  {tags.map(tag => (
                    <div
                      key={tag}
                      className="flex items-center gap-1 bg-primary/10 text-primary px-2 py-1 rounded-md text-sm"
                    >
                      <span>{tag}</span>
                      <button
                        type="button"
                        onClick={() => removeTag(tag)}
                        className="text-xs hover:text-red-500"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>

                {/* Input */}
                <Input
                  placeholder="Type a tag and press Enter"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleTagKeyDown}
                />
              </div>

              <div className="space-y-4">
                <div className="space-y-4">
                  <div className="p-4 border rounded-lg relative space-y-3 bg-slate-50/50">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold uppercase text-muted-foreground">
                        Notes
                      </span>
                    </div>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <textarea
                          placeholder="Enter notes here..."
                          value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                          className="flex min-h-[100px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                          required
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Upload CSV Contact List *
              </label>
              <div className="space-y-3">
                <input
                  type="file"
                  id="csv-upload"
                  accept=".csv"
                  hidden
                  onChange={handleCsvUpload}
                />
                {!csvFile ? (
                  <label
                    htmlFor="csv-upload"
                    className="flex flex-col items-center justify-center w-full p-6 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors cursor-pointer"
                  >
                    <Upload className="w-8 h-8 text-gray-400 mb-2" />
                    <p className="text-sm text-gray-600">Click to upload contacts CSV</p>
                    <p className="text-xs text-gray-500 mt-1">CSV file with phone numbers</p>
                  </label>
                ) : (
                  <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-green-600" />
                      <div>
                        <p className="text-sm font-medium text-green-900">{csvFile.name}</p>
                        <p className="text-xs text-green-600">{(csvFile.size / 1024).toFixed(2)} KB</p>
                      </div>
                    </div>
                    <button
                      onClick={handleRemoveCsv}
                      className="p-1 hover:bg-green-100 rounded transition-colors"
                    >
                      <X className="w-4 h-4 text-green-700" />
                    </button>
                  </div>
                )}
              </div>

              {csvFile && (
                <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-700">
                    <FileText className="w-4 h-4 inline mr-1" />
                    Contacts will be extracted from: <strong>{csvFile.name}</strong> and added in the background
                  </p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={createContactIsPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={createContactIsPending}>
              {createContactIsPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Create Contact
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateContactModal;
